import React, { useState, useEffect, useCallback } from 'react'
import { router } from '@inertiajs/react'
import { RefreshCw, Sparkles, X } from 'lucide-react'
import { cn } from '@/lib/utils'

export default function CalendarUpdateBanner() {
  const [visible, setVisible] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const [reloading, setReloading] = useState(false)

  const handleRatesUpdated = useCallback(() => {
    setDismissed(false)
    setVisible(true)
  }, [])

  useEffect(() => {
    window.addEventListener('crown:rates-updated', handleRatesUpdated)
    return () => window.removeEventListener('crown:rates-updated', handleRatesUpdated)
  }, [handleRatesUpdated])

  const handleReload = useCallback(() => {
    setReloading(true)
    router.reload({
      onFinish: () => {
        setVisible(false)
        setReloading(false)
      },
    })
  }, [])

  const handleDismiss = useCallback(() => {
    setDismissed(true)
    setTimeout(() => setVisible(false), 300)
  }, [])

  if (!visible) return null

  return (
    <div
      className={cn(
        'mb-5 overflow-hidden rounded-xl border shadow-sm',
        'border-teal-200/80 bg-gradient-to-r from-teal-50 via-white to-teal-50/50',
        'animate-toast-in',
        dismissed && 'animate-toast-out',
      )}
    >
      {/* Shimmer accent line at top */}
      <div className="h-[2px] bg-gradient-to-r from-transparent via-teal-400 to-transparent animate-shimmer-slow" />

      <div className="flex items-center gap-3 px-4 py-3">
        {/* Animated icon */}
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-teal-100/80 shadow-[0_0_10px_rgba(13,148,136,0.15)]">
          <Sparkles className="h-4 w-4 text-teal-600" />
        </div>

        {/* Message */}
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-foreground">
            New rates available
          </p>
          <p className="text-xs text-muted-foreground">
            Rates have been recomputed since you loaded this page.
          </p>
        </div>

        {/* Actions */}
        <div className="flex shrink-0 items-center gap-2">
          <button
            onClick={handleReload}
            disabled={reloading}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5',
              'bg-teal-600 text-white text-xs font-semibold',
              'shadow-sm shadow-teal-600/20',
              'transition-all hover:bg-teal-700 hover:shadow-md hover:shadow-teal-600/25',
              'active:scale-[0.97]',
              'disabled:opacity-60 disabled:pointer-events-none',
            )}
          >
            <RefreshCw className={cn('h-3 w-3', reloading && 'animate-spin')} />
            {reloading ? 'Refreshing...' : 'Refresh'}
          </button>

          <button
            onClick={handleDismiss}
            className="rounded-md p-1 text-muted-foreground/50 transition-colors hover:text-muted-foreground hover:bg-muted/50"
            aria-label="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  )
}
